import { Text } from "@asayinc/component-library";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { Card } from "src/components/atoms/Card";
import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LabelField } from "src/components/molecules/LabelField";
import {
  LoadingCard,
  LoadingStatsHeaderCard,
} from "src/components/molecules/Loading";
import {
  PROXY_EVENT_LABEL,
  MEETING_FORMAT_LABEL,
} from "src/constants/labels/proxyEventLabel";
import { useProxyEventQuery } from "src/store/proxyEvent";
import { isVotingFiling } from "src/utils/proxyEvent/isVotingFiling";

export function ProxyEventDetailsTab() {
  const { id } = useParams() as { id: string };
  const { data: proxyEvent, isLoading, error } = useProxyEventQuery({ id });

  if (error || (!isLoading && !proxyEvent)) {
    return <ErrorState />;
  }

  if (isLoading) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <LoadingStatsHeaderCard />
        <Box sx={{ display: "flex", mt: 8 }}>
          <Box sx={{ flexGrow: 1, flexBasis: 0, mr: 12 }}>
            <LoadingCard />
          </Box>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <LoadingCard />
          </Box>
        </Box>
      </ScrollContainer>
    );
  }

  const {
    securities,
    recordDate,
    type,
    jobNumber,
    solicitor,
    tabulator,
    solicitorContacts,
    tabulatorContacts,
    meetingDate,
    meetingTimezone,
    voteCutoff,
    voteCutoffTimezone,
    meetingFormats,
    deliveryMethod,
  } = proxyEvent;

  const isVoting = isVotingFiling(proxyEvent);

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Card title="Proxy Event Details">
        <Stack sx={{ mt: 6, px: 6 }} direction="row">
          <Box sx={{ width: "25%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Type
            </Text>
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Text variant="h5">{PROXY_EVENT_LABEL[type]}</Text>
            </Box>
          </Box>
          <Box sx={{ width: "25%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              CUSIPs
            </Text>
            <Text variant="h5">
              {securities.map((security) => security.cusip).join(", ")}
            </Text>
          </Box>
          <Box sx={{ width: "25%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Record Date
            </Text>
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Text variant="h5">{dayjs(recordDate).format("MM/DD/YYYY")}</Text>
            </Box>
          </Box>
          <Box sx={{ width: "25%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Job Number
            </Text>
            <Text variant="h5">{jobNumber || "-"}</Text>
          </Box>
        </Stack>
      </Card>
      <Box sx={{ display: "flex", mt: 8 }}>
        <Box sx={{ flexGrow: 1, flexBasis: 0, mr: 12 }}>
          <Card title="Meeting Information">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
              }}
              isLabelBold={true}
              label="Meeting Date"
              value={dayjs(meetingDate).format("MMMM DD, YYYY hh:mma")}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Timezone"
              value={meetingTimezone}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
              }}
              isLabelBold={true}
              label="Vote Cutoff"
              value={dayjs(voteCutoff).format("MMMM DD, YYYY hh:mma")}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Timezone"
              value={voteCutoffTimezone}
            />
            {meetingFormats.map((meetingFormat) => (
              <>
                <LabelField
                  sx={{
                    py: 5,
                    px: 4,
                    borderBottom: "1px solid #e0e0e0",
                    fontWeight: 600,
                  }}
                  isLabelBold={true}
                  label="Meeting Format"
                  value={MEETING_FORMAT_LABEL[meetingFormat.format]}
                />
                <LabelField
                  sx={{
                    py: 5,
                    pr: 4,
                    pl: 4,
                    ml: 9,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  isValueBold={false}
                  label={meetingFormat.value}
                  value={""}
                />
                <LabelField
                  sx={{
                    py: 5,
                    pr: 4,
                    pl: 4,
                    ml: 9,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  isValueBold={false}
                  label={"Format allows voting"}
                  value={meetingFormat.canVote === "true" ? "Yes" : "No"}
                />
              </>
            ))}
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Notice and Access"
              isLabelBold={true}
              value={deliveryMethod === "notice_and_access" ? "True" : "False"}
            />
          </Card>
        </Box>
        <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
          <Card title="Intermediaries">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Solicitor"
              isLabelBold={true}
              value={solicitor?.name || ""}
            />
            {solicitorContacts?.map((contact) => (
              <LabelField
                key={contact.id}
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label={contact.name}
                value={contact.email}
              />
            ))}
            {isVoting && (
              <>
                <LabelField
                  sx={{
                    py: 5,
                    px: 4,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  label="Tabulator"
                  isLabelBold={true}
                  value={tabulator?.name || ""}
                />
                {tabulatorContacts?.map((contact) => (
                  <LabelField
                    key={contact.id}
                    sx={{
                      py: 5,
                      pr: 4,
                      pl: 4,
                      ml: 9,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                    isValueBold={false}
                    label={contact.name}
                    value={contact.email}
                  />
                ))}
              </>
            )}
          </Card>
        </Box>
      </Box>
    </ScrollContainer>
  );
}
